import React, { useEffect, useState } from "react";
import { api, euro, API, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CreditCard, ExternalLink, Copy, QrCode, Mail } from "lucide-react";

export default function PaymentsHistory() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [links, setLinks] = useState([]);
  const [qr, setQr] = useState(null);
  const [resend, setResend] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/payment-links").then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const copyLink = (url) => { navigator.clipboard.writeText(url); toast.success("Link copiato"); };

  const openResend = (l) => { setResend(l); setEmail(l.contact_email || ""); };
  const sendEmail = async () => {
    if (!email.trim()) { toast.error("Inserisci un'email"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/payment-links/${resend.id}/resend`, { email: email.trim() });
      toast.success(data.email_sent ? `Link inviato a ${data.email}` : "Email non consegnata (indirizzo demo/non valido)");
      setResend(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <div className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-primary" /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Incassi</span></div>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">{isAdmin ? "Log Pagamenti" : "Pagamenti"}</h1>
        <p className="text-sm text-muted-foreground mt-1">Link e QR di pagamento generati: riaprili, mostrali al cliente o inviali via email.</p>
      </div>

      <Card className="glow-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Descrizione</TableHead><TableHead>Importo</TableHead><TableHead>Stato</TableHead>
            <TableHead>Contatto</TableHead><TableHead>Data</TableHead><TableHead className="text-right">Azioni</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {links.map((l) => (
              <TableRow key={l.id} data-testid={`payment-row-${l.id}`}>
                <TableCell className="font-medium">{l.description}</TableCell>
                <TableCell className="font-medium">{euro(l.amount)}</TableCell>
                <TableCell>
                  {l.status === "paid"
                    ? <Badge className="bg-teal-600 hover:bg-teal-600" data-testid={`payment-status-${l.id}`}>Pagato</Badge>
                    : <Badge className="bg-amber-500 hover:bg-amber-500 text-black" data-testid={`payment-status-${l.id}`}>{({ paid: "Pagato", refunded: "Rimborsato", refund_processing: "Rimborso da verificare", expired: "Scaduto" })[l.status] || "In attesa"}</Badge>}
                  {l.sync_error && <p className="text-xs text-amber-600 mt-1" data-testid={`payment-sync-error-${l.id}`}>{l.sync_error}</p>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{l.contact_email || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleDateString("it-IT") : "—"}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost" title="Apri link" onClick={() => window.open(l.checkout_url, "_blank")} data-testid={`payment-open-${l.id}`}><ExternalLink className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" title="Copia link" onClick={() => copyLink(l.checkout_url)} data-testid={`payment-copy-${l.id}`}><Copy className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" title="Mostra QR" onClick={() => setQr(l)} data-testid={`payment-qr-${l.id}`}><QrCode className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" title="Invia email" onClick={() => openResend(l)} data-testid={`payment-email-${l.id}`}><Mail className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {links.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nessun link di pagamento generato.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>QR di pagamento</DialogTitle></DialogHeader>
          {qr && (
            <div className="flex flex-col items-center gap-3" data-testid="payment-qr-dialog">
              <img src={`${BACKEND_URL}${qr.qr_url}`} alt="QR pagamento" className="w-56 h-56 rounded-lg bg-white p-3" />
              <p className="text-sm text-muted-foreground text-center">{qr.description} · <strong>{euro(qr.amount)}</strong></p>
              <Button variant="outline" className="w-full gap-1" onClick={() => copyLink(qr.checkout_url)}><Copy className="w-4 h-4" /> Copia link</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!resend} onOpenChange={(o) => !o && setResend(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Invia link via email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email destinatario</Label>
              <Input type="email" className="mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@email.it" data-testid="payment-resend-email" />
            </div>
            <Button className="w-full gap-1" disabled={loading} onClick={sendEmail} data-testid="payment-resend-send"><Mail className="w-4 h-4" /> {loading ? "Invio…" : "Invia link"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
